import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import Library from "@/src/components/Library.tsx";
import MarketplaceExportModal from "@/src/components/MarketplaceExportModal.tsx";
import { useProjectContext } from "@/src/context/ProjectContext.tsx";
import { deleteGeneratedImage } from "@/src/services/studioPersistenceService.ts";
import { GeneratedContent } from "@/src/types.ts";

const ProjectLibrary: React.FC = () => {
  const {
    studioProducts,
    setStudioProducts,
    multiProductScenes,
    setMultiProductScenes,
    handleDownloadImage,
    setActiveViewer,
    isLoadingProducts,
  } = useProjectContext();
  const { projectId } = Route.useParams();
  const [exportTarget, setExportTarget] = React.useState<{
    content: GeneratedContent;
    projectId: string;
    productId: string;
  } | null>(null);

  // Handlers needed for Library that modify studioProducts and multiProductScenes
  const handleDeleteImage = (productId: string, contentId: string) => {
    // Try deleting from products first
    const product = studioProducts.find((p) => p.id === productId);
    if (product) {
      const contentToDelete = product.generatedContent.find(
        (c) => c.id === contentId
      );
      if (!contentToDelete) return;

      setStudioProducts((prev) => {
        const productToUpdate = prev.find((p) => p.id === productId);
        if (!productToUpdate) return prev;

        // Check if the deleted content is a background-removed PNG
        const isBackgroundRemoved =
          contentToDelete.tags?.includes("No Background") ||
          contentToDelete.tags?.includes("Transparent") ||
          contentToDelete.imageType === "Background Removal";

        // Check if the current product.image matches the deleted content
        // Match by filename, storagePath, or by comparing base64 data (if content.image is a data URL)
        const imageMatchesDeletedContent =
          (contentToDelete.filename &&
            productToUpdate.image.name === contentToDelete.filename) ||
          (contentToDelete.storagePath &&
            productToUpdate.image.storagePath &&
            contentToDelete.storagePath === productToUpdate.image.storagePath);

        // Filter out the deleted content
        const updatedGeneratedContent = productToUpdate.generatedContent.filter(
          (c) => c.id !== contentId
        );

        // Check if any background-removed PNGs remain
        const hasRemainingBackgroundRemoved = updatedGeneratedContent.some(
          (c) =>
            c.tags?.includes("No Background") ||
            c.tags?.includes("Transparent") ||
            c.imageType === "Background Removal"
        );

        // Check if current product.image is a background-removed PNG
        const currentImageIsBackgroundRemoved =
          productToUpdate.image.mimeType?.toLowerCase().includes("image/png") &&
          (productToUpdate.image.name?.toLowerCase().includes("no_bg") ||
            productToUpdate.image.name?.toLowerCase().includes("nobg") ||
            productToUpdate.image.name?.toLowerCase().endsWith("_nobg.png"));

        // If we deleted a background-removed PNG that matches the current image,
        // OR if current image is a background-removed PNG and all are deleted,
        // revert to original image
        const shouldRevertToOriginal =
          !hasRemainingBackgroundRemoved &&
          ((isBackgroundRemoved && imageMatchesDeletedContent) ||
            currentImageIsBackgroundRemoved);

        return prev.map((p) => {
          if (p.id !== productId) return p;
          return {
            ...p,
            generatedContent: updatedGeneratedContent,
            ...(shouldRevertToOriginal ? { image: p.originalImage } : {}),
          };
        });
      });

      deleteGeneratedImage(contentId, contentToDelete.image).catch(
        console.error
      );
      return;
    }

    // If not found in products, try scenes
    const scene = multiProductScenes.find((s) => s.id === productId);
    if (scene) {
      const contentToDelete = scene.generatedContent.find(
        (c) => c.id === contentId
      );
      setMultiProductScenes((prev) =>
        prev.map((s) => {
          if (s.id !== productId) return s;
          return {
            ...s,
            generatedContent: s.generatedContent.filter(
              (c) => c.id !== contentId
            ),
          };
        })
      );
      deleteGeneratedImage(contentId, contentToDelete?.image).catch(
        console.error
      );
    }
  };

  const handleViewImage = (productId: string, contentId: string) => {
    // Check products
    const product = studioProducts.find((p) => p.id === productId);
    if (product) {
      const content = product.generatedContent.find((c) => c.id === contentId);
      if (content) {
        setActiveViewer({ content, productId, currentContentId: contentId });
        return;
      }
    }

    // Check multiproduct scenes
    const scene = multiProductScenes.find((s) => s.id === productId);
    if (scene) {
      const content = scene.generatedContent.find((c) => c.id === contentId);
      if (content) {
        setActiveViewer({ content, productId, currentContentId: contentId });
        return;
      }
    }
  };

  const handleExportMarketplace = (productId: string, contentId: string) => {
    // Check products first
    const product = studioProducts.find((p) => p.id === productId);
    if (product) {
      const content = product.generatedContent.find((c) => c.id === contentId);
      if (content) {
        setExportTarget({ content, projectId, productId });
        return;
      }
    }

    // Check multiproduct scenes
    const scene = multiProductScenes.find((s) => s.id === productId);
    if (scene) {
      const content = scene.generatedContent.find((c) => c.id === contentId);
      if (content) {
        setExportTarget({ content, projectId, productId });
        return;
      }
    }
  };

  // Convert multiproduct scenes to look like products for library display
  const scenesAsProducts = React.useMemo(() => {
    return multiProductScenes.map((scene) => ({
      id: scene.id,
      image: { id: "", base64: "", mimeType: "image/png", name: "" },
      originalImage: {
        id: "",
        base64: "",
        mimeType: "image/png",
        name: scene.name,
      },
      info: "multiproduct",
      itemCount: scene.products.length,
      isSelected: false,
      isProcessing: false,
      generatedContent: scene.generatedContent,
      isGenerating: scene.isGenerating,
      error: scene.error,
      linkedProductIds: scene.products.map((p) => p.productId),
    }));
  }, [multiProductScenes]);

  // Combine regular products and scenes for library display
  const allProducts = React.useMemo(() => {
    return [...studioProducts, ...scenesAsProducts];
  }, [studioProducts, scenesAsProducts]);

  return (
    <div className="flex-grow h-full bg-gray-50/50 flex flex-col relative overflow-hidden">
      <Library
        products={allProducts}
        onViewImage={handleViewImage}
        onDownloadImage={handleDownloadImage}
        onDeleteImage={handleDeleteImage}
        onExportMarketplace={handleExportMarketplace}
        isLoading={isLoadingProducts}
      />
      {exportTarget && (
        <MarketplaceExportModal
          content={exportTarget.content}
          projectId={exportTarget.projectId}
          productId={exportTarget.productId}
          onExportSaved={(saved) => {
            const pid = exportTarget.productId;
            if (studioProducts.some((p) => p.id === pid)) {
              setStudioProducts((prev) =>
                prev.map((p) =>
                  p.id === pid
                    ? { ...p, generatedContent: [saved, ...p.generatedContent] }
                    : p
                )
              );
            } else {
              setMultiProductScenes((prev) =>
                prev.map((s) =>
                  s.id === pid
                    ? { ...s, generatedContent: [saved, ...s.generatedContent] }
                    : s
                )
              );
            }
          }}
          onClose={() => setExportTarget(null)}
        />
      )}
    </div>
  );
};

export const Route = createFileRoute("/projects/$projectId/library")({
  component: ProjectLibrary,
});
